// src/prototype/components/help/HelpPopover.tsx
// ============================================================
// HELP POPOVER COMPONENT — tier 1 of the help system
//
// A small `?` mark that opens a navy card with the help copy for a section
// (title, body, optional learn-more link) and, when the Ask DVA overlay is
// mounted, an "Ask more about this →" footer that opens the overlay at the
// context aliased to this helpKey (helpContexts.json `aliasOf`).
//
// Copy comes from helpContent.ts and is not changed here.
//
// Usage:
//   <HelpPopover helpKey="organization_profile" />
//   <HelpPopover helpKey="roi_model" className="ml-1" />
// ============================================================

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { HelpCircle } from 'lucide-react';
import { helpContent, HelpKey } from '../../help/helpContent';
import { AskAboutThisLink } from '../../../help/components/AskAboutThisLink';
import { HELP_CONTEXTS } from '../../../help/lib/helpData';
import { contextKeyForHelpKey } from '../../../help/lib/resolveContext';
import { useOptionalHelp } from '../../../help/useHelp';
import '../../../help/help.css';

interface HelpPopoverProps {
  /** The help content key to display */
  helpKey: HelpKey;
  /** Optional additional CSS classes for the wrapper */
  className?: string;
  /** Icon size in pixels (default: 14) */
  iconSize?: number;
}

export function HelpPopover({ helpKey, className = '', iconSize = 14 }: HelpPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const help = useOptionalHelp();

  const content = helpContent[helpKey];
  const aliasKey = contextKeyForHelpKey(helpKey, HELP_CONTEXTS);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleToggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggle();
    }
  }, [handleToggle]);

  const handleAskMore = useCallback(() => {
    setIsOpen(false);
    help?.openHelp(aliasKey ?? undefined);
  }, [help, aliasKey]);

  if (!content) {
    console.warn(`[HelpPopover] Unknown helpKey: ${helpKey}`);
    return null;
  }

  return (
    <span className={`relative inline-flex items-center ${className}`}>
      {/* Trigger: 14px glyph, gray at rest, navy on hover, coral only on focus */}
      <button
        ref={buttonRef}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        aria-label={`About ${content.title}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
        className={`
          flex size-[22px] shrink-0 items-center justify-center
          cursor-pointer transition-colors
          ${isOpen ? 'text-dvaNavy' : 'text-[#717182] hover:text-dvaNavy'}
          focus-visible:rounded-full focus-visible:ring-[3px] focus-visible:ring-dvaOrange/35 focus-visible:outline-none
        `}
      >
        <HelpCircle style={{ width: iconSize, height: iconSize }} strokeWidth={2} aria-hidden="true" />
      </button>

      {/* Popover: navy card — title, body, footer link */}
      {isOpen && (
        <span className="absolute left-1/2 top-full z-50 mt-2 -translate-x-1/2">
          <div
            ref={popoverRef}
            role="tooltip"
            className="flex w-[296px] max-w-[calc(100vw-2rem)] flex-col gap-2.5 rounded-[10px] bg-dvaNavy p-4 text-left shadow-[0_16px_36px_rgba(24,54,94,0.34)] animate-[dvaHelpPopoverIn_140ms_ease-out]"
          >
            <span className="text-[13px] font-bold text-white">{content.title}</span>
            <span className="text-[12.5px] leading-[1.6] text-white/90">{content.body}</span>
            {content.learnMoreHref && (
              <a
                href={content.learnMoreHref}
                target="_blank"
                rel="noopener noreferrer"
                className="self-start border-b border-white/35 pb-px text-[12px] font-semibold text-white/[0.82] hover:text-white"
              >
                Learn more →
              </a>
            )}
            {help && <AskAboutThisLink onClick={handleAskMore} />}
          </div>
        </span>
      )}
    </span>
  );
}

export default HelpPopover;
